/**
 * PaymentSuccessScreen — Polls for license activation after Stripe checkout.
 *
 * UI-INV-005: Leaf component, props only (no local UI state beyond pollState).
 * Shows a pulse dot while polling, a brief "access granted" moment when activated,
 * and a retry/contact view after MAX_POLLS attempts.
 * UI-INV-005 exception: useLicense is permitted here — polling for activation is
 * the screen's core responsibility; the hook cannot be injected as a prop.
 */
import { useCallback, useEffect, useState } from "react";
import { useLicense } from "../auth/useLicense";
import styles from "./PaymentSuccessScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { PrimaryAction } from "../primitives/PrimaryAction";
import { SecondaryAction } from "../primitives/SecondaryAction";
import { TIMING } from "../constants";

const MAX_POLLS = 5;

type PollState = "polling" | "activated" | "timeout";

export interface PaymentSuccessScreenProps {
  onActivated: () => void;
  onTimeout: () => void;
}

export function PaymentSuccessScreen({
  onActivated,
  onTimeout,
}: PaymentSuccessScreenProps) {
  const { active, refresh } = useLicense();
  const [pollState, setPollState] = useState<PollState>("polling");
  const [pollCount, setPollCount] = useState(0);

  const resetPolling = useCallback(() => {
    setPollState("polling");
    setPollCount(0);
  }, []);

  // Polling loop — advances pollCount until active or MAX_POLLS reached.
  useEffect(() => {
    if (pollState !== "polling") return;
    if (active) { setPollState("activated"); return; }
    if (pollCount >= MAX_POLLS) { setPollState("timeout"); return; }

    refresh();
    const timer = setTimeout(() => setPollCount((n) => n + 1), TIMING.PAYMENT_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [pollState, pollCount, active, refresh]);

  // Dismiss after showing "activated" — separate effect so the polling
  // cleanup can't cancel the dismiss timer mid-transition.
  useEffect(() => {
    if (pollState !== "activated") return;
    const timer = setTimeout(onActivated, TIMING.PAYMENT_ACTIVATED_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [pollState, onActivated]);

  if (pollState === "activated") {
    return (
      <div className={styles.screen} data-testid={TEST_IDS.PAYMENT_SUCCESS_ACTIVATED}>
        <div className={styles.meta}>
          <span className={styles.metaLabel}>The door is open</span>
        </div>
        <div className={styles.content}>
          <h1 className={styles.heading}>The cushion is ready.</h1>
        </div>
      </div>
    );
  }

  if (pollState === "timeout") {
    return (
      <div className={styles.screen} data-testid={TEST_IDS.PAYMENT_SUCCESS_TIMEOUT}>
        <div className={styles.meta}>
          <span className={styles.metaLabel}>Almost there</span>
        </div>
        <div className={styles.content}>
          <h1 className={styles.heading}>Still activating.</h1>
          <p className={styles.body}>
            This can take a moment. Try again, or reach out if it persists.
          </p>
          <div className={styles.rule} />
          <div className={styles.ctas}>
            <PrimaryAction onClick={resetPolling}>Try again</PrimaryAction>
            <SecondaryAction onClick={onTimeout} testId={TEST_IDS.PAYMENT_SUCCESS_REACH_OUT_BTN}>Reach out</SecondaryAction>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.screen} data-testid={TEST_IDS.PAYMENT_SUCCESS_SCREEN}>
      <div className={styles.meta}>
        <span className={styles.metaLabel}>Payment confirmed</span>
      </div>
      <div className={styles.content}>
        <h1 className={styles.heading}>Opening the door.</h1>
        <div className={styles.pulse} aria-hidden />
      </div>
    </div>
  );
}
